import { useEffect, useState, useCallback, useRef } from 'react';
import {
  getAuthenticatedSupabaseClient,
  refreshSupabaseRealtimeAuth,
  supabase as publicSupabaseClient,
} from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';
import { queryKeys } from '@/lib/queryKeys';
import { getOrganizationContext } from '@/lib/auth';
import { annotationQueryKey, wasAnnotationRecentlyMutated } from './useAnnotations';

interface CollaborationUser {
  id: string;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  last_active: string;
  isViewer?: boolean; // True for public link viewers
}

// Track if we've already warned about broadcast errors in development
let broadcastErrorWarned = false;

// Circuit breaker for broadcast failures (e.g., staging branch 401s)
let broadcastFailureCount = 0;
const BROADCAST_FAILURE_THRESHOLD = 3;
const REALTIME_AUTH_REFRESH_INTERVAL_MS = 45_000;

type BroadcastResult = 'ok' | 'timed out' | 'error';

interface BroadcastChannel {
  send(message: { type: 'broadcast'; event: string; payload: unknown }): Promise<BroadcastResult>;
}

// Ephemeral drag state broadcast between users (not persisted to DB)
export interface RemoteDragState {
  userId: string;
  userName: string;
  elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel';
  elementId: string;
  x: number;
  y: number;
  timestamp: number;
}

// Cursor position broadcast between users
export interface RemoteCursor {
  userId: string;
  userName: string;
  x: number;
  y: number;
  timestamp: number;
}

// Selection broadcast between users
export interface RemoteSelection {
  userId: string;
  userName: string;
  elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel';
  elementIds: string[];
  timestamp: number;
}

interface RealtimeCollaborationProps {
  floorPlanId: string;
  isPublicView?: boolean;
  onFixtureUpdate?: (payload: any) => void;
  onCircuitUpdate?: (payload: any) => void;
  onControlUpdate?: (payload: any) => void;
  onRoomUpdate?: (payload: any) => void;
  onPanelUpdate?: (payload: any) => void;
  onProductUpdate?: (payload: any) => void;
  onAnnotationUpdate?: (payload: any) => void;
  onProductGroupUpdate?: (payload: any) => void;
  onProductConnectionUpdate?: (payload: any) => void;
}

/**
 * Helper function to safely send broadcasts with error handling
 * Local Supabase realtime broadcast endpoint doesn't work (known limitation)
 * Skips broadcasts in development, works fine in staging/production
 */
async function safeBroadcast(
  channel: BroadcastChannel,
  event: string,
  payload: unknown,
  options: {
    isChannelReady?: () => boolean;
    refreshAuth?: (forceRefresh?: boolean) => Promise<boolean>;
  } = {},
) {
  const isDevelopment = import.meta.env.DEV;
  const { isChannelReady, refreshAuth } = options;

  // Skip broadcasts entirely in local development since they don't work
  if (isDevelopment) {
    if (!broadcastErrorWarned) {
      logger.debug(
        'Realtime broadcasts are disabled in local development (use staging/production to test)',
        {
          event,
        },
      );
      broadcastErrorWarned = true;
    }
    return; // Don't attempt the broadcast at all
  }

  // Circuit breaker: stop attempting after repeated failures (e.g., staging branch 401s)
  if (broadcastFailureCount >= BROADCAST_FAILURE_THRESHOLD) {
    return;
  }

  if (isChannelReady && !isChannelReady()) {
    return;
  }

  // In staging/production, send broadcasts with error handling
  try {
    const result = await channel.send({
      type: 'broadcast',
      event,
      payload,
    });
    if (result === 'error' || result === 'timed out') {
      throw new Error(`Realtime broadcast ${result}`);
    }
    broadcastFailureCount = 0; // Reset on success
  } catch {
    if (refreshAuth) {
      try {
        const authRefreshed = await refreshAuth(true);
        if (authRefreshed && (!isChannelReady || isChannelReady())) {
          const retryResult = await channel.send({
            type: 'broadcast',
            event,
            payload,
          });
          if (retryResult !== 'error' && retryResult !== 'timed out') {
            broadcastFailureCount = 0;
            return;
          }
        }
      } catch (retryError) {
        logger.debug('Realtime broadcast auth refresh failed', retryError);
      }
    }

    broadcastFailureCount++;
    if (broadcastFailureCount === BROADCAST_FAILURE_THRESHOLD) {
      logger.warn(
        `Realtime broadcasts disabled after ${BROADCAST_FAILURE_THRESHOLD} consecutive failures (likely a branch environment without full auth support)`,
      );
    }
    // Don't throw - broadcast failures shouldn't break the app
  }
}

export const useRealtimeCollaboration = ({
  floorPlanId,
  isPublicView = false,
  onFixtureUpdate,
  onCircuitUpdate,
  onControlUpdate,
  onRoomUpdate,
  onPanelUpdate,
  onProductUpdate,
  onAnnotationUpdate,
  onProductGroupUpdate,
  onProductConnectionUpdate,
}: RealtimeCollaborationProps) => {
  const [activeUsers, setActiveUsers] = useState<CollaborationUser[]>([]);
  const [remoteDrags, setRemoteDrags] = useState<Map<string, RemoteDragState>>(new Map());
  const [remoteCursors, setRemoteCursors] = useState<Map<string, RemoteCursor>>(new Map());
  const [remoteSelections, setRemoteSelections] = useState<Map<string, RemoteSelection>>(new Map());
  const queryClient = useQueryClient();
  const channelRef = useRef<any>(null);
  const channelReadyRef = useRef(false);
  const authRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const subscriptionsRef = useRef<any[]>([]);
  const currentUserIdRef = useRef<string>('');

  // Get user info from Clerk if available
  const { userId: clerkUserId } = useClerkAuth();
  const { user: clerkUser } = useUser();

  // Generate a unique user ID for presence tracking
  const getUserId = useCallback(async () => {
    if (isPublicView) {
      return `public-${Math.random().toString(36).substr(2, 9)}`;
    }
    if (clerkUserId) {
      return clerkUserId;
    }

    const { userId } = await getOrganizationContext();
    return userId ?? 'anonymous';
  }, [clerkUserId, isPublicView]);

  // Get user display info
  const getUserInfo = useCallback(async () => {
    if (isPublicView) {
      return {
        name: 'Guest Viewer',
        email: null,
        avatar_url: null,
      };
    }
    if (clerkUser) {
      return {
        name: clerkUser.fullName || clerkUser.firstName || 'Team Member',
        email: clerkUser.primaryEmailAddress?.emailAddress || null,
        avatar_url: clerkUser.imageUrl || null,
      };
    }

    return {
      name: 'Anonymous',
      email: null,
      avatar_url: null,
    };
  }, [clerkUser, isPublicView]);

  useEffect(() => {
    if (!floorPlanId) return;

    const setupRealtimeConnection = async () => {
      try {
        const userId = await getUserId();
        const userInfo = await getUserInfo();
        const supabase = isPublicView
          ? publicSupabaseClient
          : await getAuthenticatedSupabaseClient();
        const realtimeAuthReady = isPublicView ? true : await refreshSupabaseRealtimeAuth();

        if (!realtimeAuthReady && !isPublicView) {
          logger.warn('Realtime auth token unavailable; collaboration broadcasts will be disabled');
        }

        // Store current user ID for broadcast filtering
        currentUserIdRef.current = userId;

        // Create a presence channel for this floor plan
        const channel = supabase.channel(`floor-plan:${floorPlanId}`);
        channelRef.current = channel;

        // Track presence
        channel
          .on('presence', { event: 'sync' }, () => {
            const state = channel.presenceState();
            const users: CollaborationUser[] = [];

            Object.keys(state).forEach(key => {
              const presences = state[key] as any[];
              presences.forEach(presence => {
                users.push({
                  id: presence.user_id,
                  name: presence.user_name,
                  email: presence.user_email,
                  avatar_url: presence.avatar_url,
                  last_active: presence.online_at,
                  isViewer: presence.is_viewer,
                });
              });
            });

            setActiveUsers(users);
          })
          .on('presence', { event: 'join' }, ({ newPresences }) => {
            const isSelf = newPresences.some((presence: any) => presence.user_id === userId);
            if (isSelf) return;

            const joinedUser = newPresences[0] as any;
            const userName =
              joinedUser?.user_name || (joinedUser?.is_viewer ? 'A guest' : 'A team member');
            toast.info(`${userName} joined the floor plan`);
          })
          .on('presence', { event: 'leave' }, ({ leftPresences }) => {
            const isSelf = leftPresences.some((presence: any) => presence.user_id === userId);
            if (isSelf) return;

            const leftUser = leftPresences[0] as any;
            const userName =
              leftUser?.user_name || (leftUser?.is_viewer ? 'A guest' : 'A team member');
            toast.info(`${userName} left the floor plan`);
          })
          // Listen for drag broadcasts (ephemeral, not persisted)
          .on('broadcast', { event: 'element:drag' }, ({ payload }: any) => {
            // Ignore our own drags
            if (payload.userId === userId) return;

            logger.log('[Broadcast] Remote drag:', payload);

            // Update remote drag state
            setRemoteDrags(prev => {
              const newMap = new Map(prev);
              const key = `${payload.elementType}:${payload.elementId}`;
              newMap.set(key, {
                userId: payload.userId,
                userName: payload.userName,
                elementType: payload.elementType,
                elementId: payload.elementId,
                x: payload.x,
                y: payload.y,
                timestamp: Date.now(),
              });
              return newMap;
            });
          })
          // Listen for drag end broadcasts (clear remote drag state and trigger refetch)
          .on('broadcast', { event: 'element:drag:end' }, ({ payload }: any) => {
            if (payload.userId === userId) return;

            logger.log('[Broadcast] Remote drag end:', payload);

            // Remove from remote drag state
            setRemoteDrags(prev => {
              const newMap = new Map(prev);
              const key = `${payload.elementType}:${payload.elementId}`;
              newMap.delete(key);
              return newMap;
            });

            // Trigger refetch for the updated element type to sync database changes
            // This ensures the final position is immediately reflected in the UI
            const refetchCallbacks: Record<string, (() => void) | undefined> = {
              fixture: onFixtureUpdate,
              control: onControlUpdate,
              product: onProductUpdate,
              annotation: onAnnotationUpdate,
              room: onRoomUpdate,
              panel: onPanelUpdate,
            };

            const refetchCallback = refetchCallbacks[payload.elementType];
            if (refetchCallback) {
              logger.log(`[Broadcast] Triggering ${payload.elementType} refetch after drag end`);
              refetchCallback();
            }
          })
          // Listen for cursor broadcasts
          .on('broadcast', { event: 'cursor:move' }, ({ payload }: any) => {
            if (payload.userId === userId) return;

            // Update remote cursor position
            setRemoteCursors(prev => {
              const newMap = new Map(prev);
              newMap.set(payload.userId, {
                userId: payload.userId,
                userName: payload.userName,
                x: payload.x,
                y: payload.y,
                timestamp: Date.now(),
              });
              return newMap;
            });
          })
          // Listen for cursor leave broadcasts
          .on('broadcast', { event: 'cursor:leave' }, ({ payload }: any) => {
            if (payload.userId === userId) return;

            // Remove cursor from remote cursors
            setRemoteCursors(prev => {
              const newMap = new Map(prev);
              newMap.delete(payload.userId);
              return newMap;
            });
          })
          // Listen for selection broadcasts
          .on('broadcast', { event: 'selection:change' }, ({ payload }: any) => {
            if (payload.userId === userId) return;

            // Update remote selection
            setRemoteSelections(prev => {
              const newMap = new Map(prev);
              if (payload.elementIds.length === 0) {
                // Clear selection for this user
                newMap.delete(payload.userId);
              } else {
                newMap.set(payload.userId, {
                  userId: payload.userId,
                  userName: payload.userName,
                  elementType: payload.elementType,
                  elementIds: payload.elementIds,
                  timestamp: Date.now(),
                });
              }
              return newMap;
            });
          });

        // Subscribe to the channel and track presence
        await channel.subscribe(async status => {
          if (status === 'SUBSCRIBED') {
            channelReadyRef.current = true;
            broadcastFailureCount = 0;
            await channel.track({
              user_id: userId,
              user_name: userInfo.name,
              user_email: userInfo.email,
              avatar_url: userInfo.avatar_url,
              online_at: new Date().toISOString(),
              is_viewer: isPublicView,
            });
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            channelReadyRef.current = false;
            logger.warn('Realtime collaboration channel is not ready', { status, floorPlanId });
          } else if (status === 'CLOSED') {
            channelReadyRef.current = false;
          }
        });

        if (!isPublicView) {
          authRefreshIntervalRef.current = setInterval(async () => {
            try {
              await refreshSupabaseRealtimeAuth();
            } catch (error) {
              channelReadyRef.current = false;
              logger.warn('Realtime auth refresh failed', { error, floorPlanId });
            }
          }, REALTIME_AUTH_REFRESH_INTERVAL_MS);
        }

        // Subscribe to data changes for all users (viewers get updates too)
        // Note: Public viewers can't make changes due to RLS policies

        // Subscribe to fixture changes
        const fixtureSubscription = supabase
          .channel(`fixtures:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'lighting_fixtures', // Correct table name
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Fixture change detected:', payload);
              logger.log(
                '[Realtime] Fixture change:',
                payload.eventType,
                payload.new || payload.old,
              );

              if (onFixtureUpdate) {
                onFixtureUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              const queryKey = queryKeys.fixtures(floorPlanId);
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'INSERT' && payload.new && Array.isArray(currentData)) {
                // Add new item to cache only if it doesn't already exist (prevents duplicates from optimistic updates)
                const exists = currentData.some((item: any) => item.id === payload.new.id);
                if (!exists) {
                  queryClient.setQueryData(queryKey, [...currentData, payload.new]);
                }
              } else if (
                payload.eventType === 'UPDATE' &&
                payload.new &&
                Array.isArray(currentData)
              ) {
                // Update item in cache immediately
                queryClient.setQueryData(
                  queryKey,
                  currentData.map((item: any) => (item.id === payload.new.id ? payload.new : item)),
                );
              } else if (
                payload.eventType === 'DELETE' &&
                payload.old &&
                Array.isArray(currentData)
              ) {
                // Enhanced DELETE handling with aggressive refetch
                logger.warn('[Realtime] DELETE event received:', {
                  table: payload.table || 'unknown',
                  deletedId: payload.old.id,
                  currentCacheSize: currentData.length,
                  deletedItem: payload.old,
                });

                // Remove deleted item from cache immediately
                const newData = currentData.filter((item: any) => item.id !== payload.old.id);
                queryClient.setQueryData(queryKey, newData);

                logger.warn('[Realtime] Cache updated after DELETE:', {
                  table: payload.table || 'unknown',
                  newCacheSize: newData.length,
                  removedCount: currentData.length - newData.length,
                });

                // Force immediate refetch to ensure sync
                queryClient.refetchQueries({
                  queryKey,
                  type: 'active',
                });
              }

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(fixtureSubscription);

        // Subscribe to circuit changes
        const circuitSubscription = supabase
          .channel(`circuits:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'circuits',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Circuit change detected:', payload);
              logger.log(
                '[Realtime] Circuit change:',
                payload.eventType,
                payload.new || payload.old,
              );

              if (onCircuitUpdate) {
                onCircuitUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              const queryKey = queryKeys.circuits(floorPlanId);
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'INSERT' && payload.new && Array.isArray(currentData)) {
                // Add new item to cache only if it doesn't already exist (prevents duplicates from optimistic updates)
                const exists = currentData.some((item: any) => item.id === payload.new.id);
                if (!exists) {
                  queryClient.setQueryData(queryKey, [...currentData, payload.new]);
                }
              } else if (
                payload.eventType === 'UPDATE' &&
                payload.new &&
                Array.isArray(currentData)
              ) {
                // Update item in cache immediately
                queryClient.setQueryData(
                  queryKey,
                  currentData.map((item: any) => (item.id === payload.new.id ? payload.new : item)),
                );
              } else if (
                payload.eventType === 'DELETE' &&
                payload.old &&
                Array.isArray(currentData)
              ) {
                // Enhanced DELETE handling with aggressive refetch
                logger.warn('[Realtime] DELETE event received:', {
                  table: payload.table || 'unknown',
                  deletedId: payload.old.id,
                  currentCacheSize: currentData.length,
                  deletedItem: payload.old,
                });

                // Remove deleted item from cache immediately
                const newData = currentData.filter((item: any) => item.id !== payload.old.id);
                queryClient.setQueryData(queryKey, newData);

                logger.warn('[Realtime] Cache updated after DELETE:', {
                  table: payload.table || 'unknown',
                  newCacheSize: newData.length,
                  removedCount: currentData.length - newData.length,
                });

                // Force immediate refetch to ensure sync
                queryClient.refetchQueries({
                  queryKey,
                  type: 'active',
                });
              }

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(circuitSubscription);

        // Subscribe to control changes
        const controlSubscription = supabase
          .channel(`controls:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'lighting_controls',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Control change detected:', payload);
              logger.log(
                '[Realtime] Control change:',
                payload.eventType,
                payload.new || payload.old,
              );

              if (onControlUpdate) {
                onControlUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              const queryKey = queryKeys.controls(floorPlanId);
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'INSERT' && payload.new && Array.isArray(currentData)) {
                // Add new item to cache only if it doesn't already exist (prevents duplicates from optimistic updates)
                const exists = currentData.some((item: any) => item.id === payload.new.id);
                if (!exists) {
                  queryClient.setQueryData(queryKey, [...currentData, payload.new]);
                }
              } else if (
                payload.eventType === 'UPDATE' &&
                payload.new &&
                Array.isArray(currentData)
              ) {
                // Update item in cache immediately
                queryClient.setQueryData(
                  queryKey,
                  currentData.map((item: any) => (item.id === payload.new.id ? payload.new : item)),
                );
              } else if (
                payload.eventType === 'DELETE' &&
                payload.old &&
                Array.isArray(currentData)
              ) {
                // Enhanced DELETE handling with aggressive refetch
                logger.warn('[Realtime] DELETE event received:', {
                  table: payload.table || 'unknown',
                  deletedId: payload.old.id,
                  currentCacheSize: currentData.length,
                  deletedItem: payload.old,
                });

                // Remove deleted item from cache immediately
                const newData = currentData.filter((item: any) => item.id !== payload.old.id);
                queryClient.setQueryData(queryKey, newData);

                logger.warn('[Realtime] Cache updated after DELETE:', {
                  table: payload.table || 'unknown',
                  newCacheSize: newData.length,
                  removedCount: currentData.length - newData.length,
                });

                // Force immediate refetch to ensure sync
                queryClient.refetchQueries({
                  queryKey,
                  type: 'active',
                });
              }

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(controlSubscription);

        // Subscribe to room changes
        const roomSubscription = supabase
          .channel(`rooms:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'rooms',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Room change detected:', payload);

              if (onRoomUpdate) {
                onRoomUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              const queryKey = queryKeys.rooms(floorPlanId);
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'INSERT' && payload.new && Array.isArray(currentData)) {
                // Add new item to cache only if it doesn't already exist (prevents duplicates from optimistic updates)
                const exists = currentData.some((item: any) => item.id === payload.new.id);
                if (!exists) {
                  queryClient.setQueryData(queryKey, [...currentData, payload.new]);
                }
              } else if (
                payload.eventType === 'UPDATE' &&
                payload.new &&
                Array.isArray(currentData)
              ) {
                // Update item in cache immediately
                queryClient.setQueryData(
                  queryKey,
                  currentData.map((item: any) => (item.id === payload.new.id ? payload.new : item)),
                );
              } else if (
                payload.eventType === 'DELETE' &&
                payload.old &&
                Array.isArray(currentData)
              ) {
                // Enhanced DELETE handling with aggressive refetch
                logger.warn('[Realtime] DELETE event received:', {
                  table: payload.table || 'unknown',
                  deletedId: payload.old.id,
                  currentCacheSize: currentData.length,
                  deletedItem: payload.old,
                });

                // Remove deleted item from cache immediately
                const newData = currentData.filter((item: any) => item.id !== payload.old.id);
                queryClient.setQueryData(queryKey, newData);

                logger.warn('[Realtime] Cache updated after DELETE:', {
                  table: payload.table || 'unknown',
                  newCacheSize: newData.length,
                  removedCount: currentData.length - newData.length,
                });

                // Force immediate refetch to ensure sync
                queryClient.refetchQueries({
                  queryKey,
                  type: 'active',
                });
              }

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(roomSubscription);

        // Subscribe to panel changes
        const panelSubscription = supabase
          .channel(`panels:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'panels',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Panel change detected:', payload);
              logger.log('[Realtime] Panel change:', payload.eventType, payload.new || payload.old);

              if (onPanelUpdate) {
                onPanelUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              const queryKey = queryKeys.panels(floorPlanId);
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'INSERT' && payload.new && Array.isArray(currentData)) {
                // Add new item to cache only if it doesn't already exist (prevents duplicates from optimistic updates)
                const exists = currentData.some((item: any) => item.id === payload.new.id);
                if (!exists) {
                  queryClient.setQueryData(queryKey, [...currentData, payload.new]);
                }
              } else if (
                payload.eventType === 'UPDATE' &&
                payload.new &&
                Array.isArray(currentData)
              ) {
                // Update item in cache immediately
                queryClient.setQueryData(
                  queryKey,
                  currentData.map((item: any) => (item.id === payload.new.id ? payload.new : item)),
                );
              } else if (
                payload.eventType === 'DELETE' &&
                payload.old &&
                Array.isArray(currentData)
              ) {
                // Enhanced DELETE handling with aggressive refetch
                logger.warn('[Realtime] DELETE event received:', {
                  table: payload.table || 'unknown',
                  deletedId: payload.old.id,
                  currentCacheSize: currentData.length,
                  deletedItem: payload.old,
                });

                // Remove deleted item from cache immediately
                const newData = currentData.filter((item: any) => item.id !== payload.old.id);
                queryClient.setQueryData(queryKey, newData);

                logger.warn('[Realtime] Cache updated after DELETE:', {
                  table: payload.table || 'unknown',
                  newCacheSize: newData.length,
                  removedCount: currentData.length - newData.length,
                });

                // Force immediate refetch to ensure sync
                queryClient.refetchQueries({
                  queryKey,
                  type: 'active',
                });
              }

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(panelSubscription);

        // Subscribe to product changes (lv_products)
        const productSubscription = supabase
          .channel(`products:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'lv_products',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Product change detected:', payload);
              logger.log(
                '[Realtime] Product change:',
                payload.eventType,
                payload.new || payload.old,
              );

              if (onProductUpdate) {
                onProductUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              const queryKey = [...queryKeys.floorPlan(floorPlanId), 'lv-products'];
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'INSERT' && payload.new && Array.isArray(currentData)) {
                // Add new item to cache only if it doesn't already exist (prevents duplicates from optimistic updates)
                const exists = currentData.some((item: any) => item.id === payload.new.id);
                if (!exists) {
                  queryClient.setQueryData(queryKey, [...currentData, payload.new]);
                }
              } else if (
                payload.eventType === 'UPDATE' &&
                payload.new &&
                Array.isArray(currentData)
              ) {
                // Update item in cache immediately
                queryClient.setQueryData(
                  queryKey,
                  currentData.map((item: any) => (item.id === payload.new.id ? payload.new : item)),
                );
              } else if (
                payload.eventType === 'DELETE' &&
                payload.old &&
                Array.isArray(currentData)
              ) {
                // Enhanced DELETE handling with aggressive refetch
                logger.warn('[Realtime] DELETE event received:', {
                  table: payload.table || 'unknown',
                  deletedId: payload.old.id,
                  currentCacheSize: currentData.length,
                  deletedItem: payload.old,
                });

                // Remove deleted item from cache immediately
                const newData = currentData.filter((item: any) => item.id !== payload.old.id);
                queryClient.setQueryData(queryKey, newData);

                logger.warn('[Realtime] Cache updated after DELETE:', {
                  table: payload.table || 'unknown',
                  newCacheSize: newData.length,
                  removedCount: currentData.length - newData.length,
                });

                // Force immediate refetch to ensure sync
                queryClient.refetchQueries({
                  queryKey,
                  type: 'active',
                });
              }

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(productSubscription);

        // Subscribe to annotation changes (floor_plan_annotations)
        const annotationSubscription = supabase
          .channel(`annotations:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'floor_plan_annotations',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Annotation change detected:', payload);
              logger.log(
                '[Realtime] Annotation change:',
                payload.eventType,
                payload.new || payload.old,
              );

              if (onAnnotationUpdate) {
                onAnnotationUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              const queryKey = annotationQueryKey(floorPlanId, 'floor_plan');
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'INSERT' && payload.new && Array.isArray(currentData)) {
                // Add new item to cache only if it doesn't already exist (prevents duplicates from optimistic updates)
                const exists = currentData.some((item: any) => item.id === payload.new.id);
                if (!exists) {
                  queryClient.setQueryData(queryKey, [...currentData, payload.new]);
                }
              } else if (
                payload.eventType === 'UPDATE' &&
                payload.new &&
                Array.isArray(currentData)
              ) {
                // CRITICAL: Skip updates for annotations that were just mutated locally
                // This prevents flickering when dragging - the realtime event can arrive
                // before React re-renders with the new dragState
                if (wasAnnotationRecentlyMutated(payload.new.id)) {
                  logger.log(
                    '[Realtime] Annotation UPDATE - skipping recently mutated annotation:',
                    {
                      id: payload.new.id,
                    },
                  );
                  return;
                }

                // CRITICAL: Only update cache if incoming data is newer than local data
                // This prevents realtime events from overwriting in-flight mutations (style changes, text edits)
                const existingItem = currentData.find((item: any) => item.id === payload.new.id);
                const existingTimestamp = existingItem?.updated_at
                  ? new Date(existingItem.updated_at).getTime()
                  : 0;
                const incomingTimestamp = payload.new.updated_at
                  ? new Date(payload.new.updated_at).getTime()
                  : 0;

                if (incomingTimestamp > existingTimestamp) {
                  logger.log('[Realtime] Annotation UPDATE - applying newer data:', {
                    id: payload.new.id,
                    existing: existingTimestamp,
                    incoming: incomingTimestamp,
                  });
                  queryClient.setQueryData(
                    queryKey,
                    currentData.map((item: any) =>
                      item.id === payload.new.id ? payload.new : item,
                    ),
                  );
                } else {
                  logger.log('[Realtime] Annotation UPDATE - skipping stale data:', {
                    id: payload.new.id,
                    existing: existingTimestamp,
                    incoming: incomingTimestamp,
                  });
                }
              } else if (
                payload.eventType === 'DELETE' &&
                payload.old &&
                Array.isArray(currentData)
              ) {
                // Enhanced DELETE handling with aggressive refetch
                logger.warn('[Realtime] DELETE event received:', {
                  table: payload.table || 'unknown',
                  deletedId: payload.old.id,
                  currentCacheSize: currentData.length,
                  deletedItem: payload.old,
                });

                // Remove deleted item from cache immediately
                const newData = currentData.filter((item: any) => item.id !== payload.old.id);
                queryClient.setQueryData(queryKey, newData);

                logger.warn('[Realtime] Cache updated after DELETE:', {
                  table: payload.table || 'unknown',
                  newCacheSize: newData.length,
                  removedCount: currentData.length - newData.length,
                });

                // Force immediate refetch to ensure sync
                queryClient.refetchQueries({
                  queryKey,
                  type: 'active',
                });
              }

              // For annotations, use softer invalidation to avoid overwriting pending mutations
              // Don't force refetch for UPDATE events - let mutations complete first
              if (payload.eventType !== 'UPDATE') {
                queryClient.invalidateQueries({
                  queryKey,
                  refetchType: 'all',
                });
              }

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(annotationSubscription);

        // Subscribe to product group changes
        const productGroupSubscription = supabase
          .channel(`product-groups:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'lv_product_groups',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Product group change detected:', payload);
              logger.log(
                '[Realtime] Product group change:',
                payload.eventType,
                payload.new || payload.old,
              );

              if (onProductGroupUpdate) {
                onProductGroupUpdate(payload);
              }

              // Optimistic cache updates for instant feedback
              // Note: Product groups affect the lv-products query, not a separate groups query
              const queryKey = [...queryKeys.floorPlan(floorPlanId), 'lv-products'];
              const currentData = queryClient.getQueryData(queryKey);

              if (payload.eventType === 'DELETE' && payload.old && Array.isArray(currentData)) {
                // Filter out products that belong to the deleted group
                queryClient.setQueryData(
                  queryKey,
                  currentData.filter((item: any) => item.group_id !== payload.old.id),
                );
              }
              // INSERT/UPDATE of product groups doesn't directly affect products cache
              // Just invalidate to refetch and get updated group associations

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(productGroupSubscription);

        // Subscribe to product connection changes
        const productConnectionSubscription = supabase
          .channel(`product-connections:${floorPlanId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'lv_product_connections',
              filter: `floor_plan_id=eq.${floorPlanId}`,
            },
            payload => {
              logger.log('Product connection change detected:', payload);
              logger.log(
                '[Realtime] Product connection change:',
                payload.eventType,
                payload.new || payload.old,
              );

              if (onProductConnectionUpdate) {
                onProductConnectionUpdate(payload);
              }

              // Product connections affect visual relationships but not the products list itself
              // Just invalidate to refetch and get updated connection data
              const queryKey = [...queryKeys.floorPlan(floorPlanId), 'lv-products'];

              // Invalidate queries to trigger re-renders in active components
              queryClient.invalidateQueries({
                queryKey,
                refetchType: 'all',
              });

              // Toast notifications removed - visual updates are sufficient feedback
            },
          )
          .subscribe();

        subscriptionsRef.current.push(productConnectionSubscription);
      } catch (error) {
        logger.error('Error setting up realtime collaboration:', error);
      }
    };

    setupRealtimeConnection();

    // Cleanup on unmount
    return () => {
      if (authRefreshIntervalRef.current) {
        clearInterval(authRefreshIntervalRef.current);
        authRefreshIntervalRef.current = null;
      }

      // Untrack presence
      if (channelRef.current) {
        channelReadyRef.current = false;
        channelRef.current.untrack();
        channelRef.current.unsubscribe();
      }

      // Unsubscribe from all channels
      subscriptionsRef.current.forEach(subscription => {
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
      });
      subscriptionsRef.current = [];
    };
  }, [
    floorPlanId,
    isPublicView,
    queryClient,
    getUserId,
    getUserInfo,
    onFixtureUpdate,
    onCircuitUpdate,
    onControlUpdate,
    onRoomUpdate,
    onPanelUpdate,
    onProductUpdate,
    onAnnotationUpdate,
    onProductGroupUpdate,
    onProductConnectionUpdate,
  ]);

  // Broadcast drag position (ephemeral, not persisted)
  const broadcastDrag = useCallback(
    async (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementId: string,
      x: number,
      y: number,
    ) => {
      if (!channelRef.current || !currentUserIdRef.current) return;

      const userInfo = await getUserInfo();

      await safeBroadcast(
        channelRef.current,
        'element:drag',
        {
          userId: currentUserIdRef.current,
          userName: userInfo.name,
          elementType,
          elementId,
          x,
          y,
        },
        {
          isChannelReady: () => channelReadyRef.current,
          refreshAuth: refreshSupabaseRealtimeAuth,
        },
      );
    },
    [getUserInfo],
  );

  // Broadcast drag end (clear remote drag state)
  const broadcastDragEnd = useCallback(
    async (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementId: string,
    ) => {
      if (!channelRef.current || !currentUserIdRef.current) return;

      await safeBroadcast(
        channelRef.current,
        'element:drag:end',
        {
          userId: currentUserIdRef.current,
          elementType,
          elementId,
        },
        {
          isChannelReady: () => channelReadyRef.current,
          refreshAuth: refreshSupabaseRealtimeAuth,
        },
      );
    },
    [],
  );

  // Broadcast cursor position
  const broadcastCursor = useCallback(
    async (x: number, y: number) => {
      if (!channelRef.current || !currentUserIdRef.current) return;

      const userInfo = await getUserInfo();

      await safeBroadcast(
        channelRef.current,
        'cursor:move',
        {
          userId: currentUserIdRef.current,
          userName: userInfo.name,
          x,
          y,
        },
        {
          isChannelReady: () => channelReadyRef.current,
          refreshAuth: refreshSupabaseRealtimeAuth,
        },
      );
    },
    [getUserInfo],
  );

  // Broadcast cursor leave
  const broadcastCursorLeave = useCallback(async () => {
    if (!channelRef.current || !currentUserIdRef.current) return;

    await safeBroadcast(
      channelRef.current,
      'cursor:leave',
      {
        userId: currentUserIdRef.current,
      },
      {
        isChannelReady: () => channelReadyRef.current,
        refreshAuth: refreshSupabaseRealtimeAuth,
      },
    );
  }, []);

  // Broadcast selection change
  const broadcastSelection = useCallback(
    async (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementIds: string[],
    ) => {
      if (!channelRef.current || !currentUserIdRef.current) return;

      const userInfo = await getUserInfo();

      await safeBroadcast(
        channelRef.current,
        'selection:change',
        {
          userId: currentUserIdRef.current,
          userName: userInfo.name,
          elementType,
          elementIds,
        },
        {
          isChannelReady: () => channelReadyRef.current,
          refreshAuth: refreshSupabaseRealtimeAuth,
        },
      );
    },
    [getUserInfo],
  );

  return {
    activeUsers,
    remoteDrags,
    remoteCursors,
    remoteSelections,
    broadcastDrag,
    broadcastDragEnd,
    broadcastCursor,
    broadcastCursorLeave,
    broadcastSelection,
  };
};

// Export types for use in other components
export type { RemoteDragState, RemoteCursor, RemoteSelection, CollaborationUser };
